/**
 * FestivalClock — the one engine for the canvas's "Operating day" (Domain Model, TIME &
 * STRUCTURE): "Turns at 4 AM. A 1 AM teardown belongs to Saturday. Check-in, seals and playlists
 * all obey it." ADR 0006: instants are UTC; the day is computed in America/Detroit.
 *
 * Nothing here formats a time for a person — that is `@cider/ui/time`. This engine only answers
 * "which day is it, for the festival?" and "what is the wall clock in Detroit?".
 */

export const FESTIVAL_ZONE = 'America/Detroit';
/** The operating day turns at 04:00 Detroit. */
export const DAY_TURNS_AT_HOUR = 4;

export interface WallClock {
  year: number;
  month: number; // 1–12
  day: number; // 1–31
  hour: number; // 0–23
  minute: number;
  second: number;
  /** 'EST' or 'EDT' */
  zone: string;
}

const parts = new Intl.DateTimeFormat('en-US', {
  timeZone: FESTIVAL_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',
});

/** The wall clock in Detroit for an instant. */
export function wallClock(at: Date): WallClock {
  const got: Record<string, string> = {};
  for (const p of parts.formatToParts(at)) got[p.type] = p.value;
  return {
    year: Number(got['year']),
    month: Number(got['month']),
    day: Number(got['day']),
    hour: Number(got['hour']) % 24,
    minute: Number(got['minute']),
    second: Number(got['second']),
    zone: got['timeZoneName'] ?? '',
  };
}

/** An operating day is a calendar date in Detroit, written `YYYY-MM-DD`. */
export type OperatingDay = `${number}-${string}-${string}`;

const pad = (n: number): string => String(n).padStart(2, '0');

/**
 * The operating day an instant belongs to: the Detroit calendar date, unless the Detroit wall
 * clock is before 04:00, in which case the previous date. Both 1:30 AMs on the fall-back night
 * are before 4 AM, so both belong to the day before; the missing 2 AM in March changes nothing.
 */
export function operatingDay(at: Date): OperatingDay {
  const w = wallClock(at);
  if (w.hour >= DAY_TURNS_AT_HOUR) return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
  // Step back 24 hours from midday of the same date: safe across DST because midday always exists.
  const noonUtcOfSameDate = Date.UTC(w.year, w.month - 1, w.day, 12);
  const prev = new Date(noonUtcOfSameDate - 24 * 60 * 60 * 1000);
  return `${prev.getUTCFullYear()}-${pad(prev.getUTCMonth() + 1)}-${pad(prev.getUTCDate())}`;
}

/** The Detroit calendar date an instant falls on, `YYYY-MM-DD` — a bill's date, a report's as-of; never the UTC date. */
export function calendarDay(at: Date): OperatingDay {
  const w = wallClock(at);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

/**
 * The instant whose Detroit wall clock is this date and this time — the way a 1.0 season's
 * `date` + `start` columns (local wall clock, no offset) become the UTC the schema keeps.
 * Two passes converge across both DST changes; a time that does not exist in Detroit (2:30 AM
 * on the spring-forward night) lands on the same instant as 3:30 AM, which is what a clock on
 * the wall would have read.
 */
export function instantAt(day: string, time: string): Date {
  const [y, mo, d] = day.split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  const wanted = Date.UTC(y ?? 1970, (mo ?? 1) - 1, d ?? 1, h ?? 0, mi ?? 0);
  let guess = new Date(wanted);
  for (let i = 0; i < 2; i++) {
    const w = wallClock(guess);
    const isAt = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, 0);
    if (isAt === wanted) break;
    guess = new Date(guess.getTime() + (wanted - isAt));
  }
  return guess;
}

/**
 * **The count room's day** (ADR 0023, unparked Oct 3): the first festival day that has not been
 * day-sealed — Friday stays Friday until it is sealed, whatever the hour, because Friday's money is
 * counted at one on Saturday morning. The wall clock (`today`, the operating day) decides only
 * where the record cannot: before the festival, after the last seal, and in a year with no dates.
 * A festival day still to come is never chosen ahead of today.
 */
export function countRoomDay(
  today: OperatingDay,
  festival: { friday: string; sunday: string } | null,
  sealed: ReadonlySet<string>,
): { day: OperatingDay; from: 'record' | 'clock' } {
  if (!festival) return { day: today, from: 'clock' };
  const days = festivalDays(festival);
  const open = days.find((d) => d <= today && !sealed.has(d));
  return open ? { day: open, from: 'record' } : { day: today, from: 'clock' };
}

/** Friday, Saturday, Sunday — the festival's days in order, from its Friday and Sunday. */
export function festivalDays(festival: { friday: string; sunday: string }): OperatingDay[] {
  const out: OperatingDay[] = [];
  const [y, m, d] = festival.friday.split('-').map(Number);
  for (let i = 0; ; i++) {
    const at = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + i, 12));
    const iso: OperatingDay =
      `${String(at.getUTCFullYear())}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}` as OperatingDay;
    if (iso > festival.sunday || i > 6) break;
    out.push(iso);
  }
  return out;
}

/** The festival's grounds — where the sun is reckoned from (the forecast reads the same point). */
export const FESTIVAL_SITE = { lat: 42.6261, lon: -83.0335 } as const;

/**
 * **Civil dusk** on a calendar date at the grounds (E03, Stream E — the field app and dispatch go
 * dark at civil dusk from the festival clock, never from the phone's setting): the instant the
 * sun is 6° below the horizon in the evening, by the standard sunrise equation (zenith 96°).
 * Good to a few minutes, which is all a theme needs.
 */
export function civilDusk(day: string): Date {
  const rad = Math.PI / 180;
  const [y, m, d] = day.split('-').map(Number);
  const midnight = Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  const n = Math.floor((midnight - Date.UTC(y ?? 1970, 0, 0)) / 86_400_000);
  const lonHour = FESTIVAL_SITE.lon / 15;
  const t = n + (18 - lonHour) / 24;
  const M = 0.9856 * t - 3.289;
  const L =
    (((M + 1.916 * Math.sin(M * rad) + 0.02 * Math.sin(2 * M * rad) + 282.634) % 360) + 360) % 360;
  let RA = (Math.atan(0.91764 * Math.tan(L * rad)) / rad + 360) % 360;
  RA = (RA + Math.floor(L / 90) * 90 - Math.floor(RA / 90) * 90) / 15;
  const sinDec = 0.39782 * Math.sin(L * rad);
  const cosDec = Math.cos(Math.asin(sinDec));
  const cosH =
    (Math.cos(96 * rad) - sinDec * Math.sin(FESTIVAL_SITE.lat * rad)) /
    (cosDec * Math.cos(FESTIVAL_SITE.lat * rad));
  const H = Math.acos(Math.max(-1, Math.min(1, cosH))) / rad / 15;
  const T = H + RA - 0.06571 * t - 6.622;
  const UT = (((T - lonHour) % 24) + 24) % 24;
  // West of Greenwich an evening falls after UTC midnight: an hour before noon is the next day's.
  return new Date(midnight + (UT < 12 ? UT + 24 : UT) * 3_600_000);
}

export const FestivalClock = {
  zone: FESTIVAL_ZONE,
  turnsAtHour: DAY_TURNS_AT_HOUR,
  wallClock,
  operatingDay,
  calendarDay,
  instantAt,
  countRoomDay,
  festivalDays,
  civilDusk,
} as const;
